import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';

const Hero = ({ onQuoteClick }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "Elevate Your Savings",
      subtitle: "Don't be surprised by hidden fees. Our prices include delivery, pickup, and damage waiver.",
      cta: "Book Now",
      action: onQuoteClick
    },
    {
      title: "Save Big on Scissor Lifts!",
      subtitle: "Compare our all-inclusive pricing to the competition and see how much you can save.",
      cta: "View Pricing",
      action: () => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })
    },
    {
      title: "Same-Day Delivery Available",
      subtitle: "New customer? No problem! Give us a call and get it delivered today.",
      cta: "Call 714-379-5545",
      action: () => window.location.href = 'tel:714-379-5545'
    },
    {
      title: "Over 600 Units in Our Fleet",
      subtitle: "We have the capability to handle almost every type of construction situation.",
      cta: "Browse Equipment",
      action: () => document.getElementById('equipment').scrollIntoView({ behavior: 'smooth' })
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <section className="relative bg-gray-900 text-white overflow-hidden min-h-[700px] md:min-h-[800px] flex items-center -mt-20 pt-20">
      {/* Video Background */}
      <div className="pointer-events-none absolute inset-0 w-full h-full top-0">
        <video 
          autoPlay 
          muted 
          loop 
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/cooper-video.mp4" type="video/mp4" />
        </video>
        {/* Gradient Overlays for depth and readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-transparent to-gray-900/40"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-900/50"></div>
      </div>

      {/* Fleet Stats Overlay - Right aligned next to content */}
      <div className="pointer-events-none hidden lg:block absolute top-1/2 right-24 -translate-y-1/2 -mt-16 z-10">
        <div className="space-y-6 animate-fade-in">
          <div className="text-right transform transition-all duration-500 hover:scale-110">
            <div className="text-6xl font-black text-[#E28724] tracking-tight leading-none mb-2" style={{textShadow: '0 4px 20px rgba(255, 140, 26, 0.5)'}}>
              600<span className="text-orange-400">+</span>
            </div>
            <div className="text-base font-semibold text-gray-100 tracking-wide">Units in Our Fleet</div>
          </div>
          <div className="text-right transform transition-all duration-500 hover:scale-110">
            <div className="text-6xl font-black text-[#E28724] tracking-tight leading-none mb-2" style={{textShadow: '0 4px 20px rgba(255, 140, 26, 0.5)'}}>
              25<span className="text-orange-400">+</span>
            </div>
            <div className="text-base font-semibold text-gray-100 tracking-wide">Years in Business</div>
          </div>
          <div className="text-right transform transition-all duration-500 hover:scale-110">
            <div className="text-6xl font-black text-[#E28724] tracking-tight leading-none mb-2" style={{textShadow: '0 4px 20px rgba(255, 140, 26, 0.5)'}}>
              100<span className="text-orange-400">%</span>
            </div>
            <div className="text-base font-semibold text-gray-100 tracking-wide leading-tight">Final Pricing<br/>No Add-ons. No surprises.</div>
          </div>
        </div>
      </div>
      
      {/* Slide Content */}
      <div className="relative z-10 h-full flex items-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 -mt-12">
        <div className="max-w-3xl w-full">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`transition-all duration-1000 ${
                index === currentSlide 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 absolute translate-x-16 pointer-events-none'
              }`}
            >
              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black mb-6 md:mb-8 leading-none tracking-tight" 
                  style={{textShadow: '0 4px 30px rgba(0, 0, 0, 0.8)'}}>
                {slide.title}
              </h1>
              <p className="text-xl sm:text-2xl md:text-3xl mb-8 md:mb-12 text-gray-100 font-light leading-relaxed max-w-2xl" 
                 style={{textShadow: '0 2px 20px rgba(0, 0, 0, 0.8)'}}>
                {slide.subtitle}
              </p>
              
              <Button 
                onClick={slide.action}
                size="lg"
                className="bg-[#E28724] hover:bg-[#e67d0f] text-white px-10 sm:px-12 py-6 sm:py-8 text-lg sm:text-xl font-bold transition-all transform hover:scale-105 hover:shadow-2xl shadow-lg w-full sm:w-auto group"
              >
                {slide.cta}
                <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              {/* Mobile Stats - Show below CTA on mobile only */}
              <div className="lg:hidden mt-12 grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-black text-[#E28724] mb-1">400+</div>
                  <div className="text-xs text-gray-200 font-medium">Scissor Lifts</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-[#E28724] mb-1">20+</div>
                  <div className="text-xs text-gray-200 font-medium">Years</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-[#E28724] mb-1">100%</div>
                  <div className="text-xs text-gray-200 font-medium">All-Inclusive</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Subtle bottom fade for smooth transition */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white/50 via-white/20 to-transparent z-10"></div>

      {/* Slide Indicators */}
      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`transition-all duration-500 rounded-full ${
              index === currentSlide 
                ? 'bg-orange-600 w-12 h-3 shadow-lg' 
                : 'bg-white/40 hover:bg-white/60 w-3 h-3'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;
